import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, MEALS, MEAL_LABELS } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { CalendarIcon, Plus, X, ChevronLeft, ChevronRight, Sparkles, GripVertical, BookOpen, ChevronDown, Search, Save, Printer, Mail, Trash2, SlidersHorizontal, Move } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import ExpiryWarningsBanner from "@/components/ExpiryWarningsBanner";
import AIMealPlanDialog from "@/components/AIMealPlanDialog";
import FeedbackButton from "@/components/FeedbackButton";
import SmartMealSuggestDialog from "@/components/SmartMealSuggestDialog";
import WeatherWidget from "@/components/WeatherWidget";
import HomeLayoutDialog from "@/components/home/HomeLayoutDialog";
import AddExtraSlotDialog from "@/components/weekly/AddExtraSlotDialog";
import AddMealDialog from "@/components/weekly/AddMealDialog";
import CreateTripDialog from "@/components/weekly/CreateTripDialog";
import MoveMealDialog from "@/components/weekly/MoveMealDialog";
import { useLayout, resolveSize } from "@/lib/homeLayout";
import { DAY_NAMES_FULL, formatDate, dateToKey, getDays, getWeekNumber, groupIntoWeeks } from "@/lib/weeklyPlanUtils";

export default function WeeklyPlan({ servings }) {
  const navigate = useNavigate();
  const { activeBoat, loadActiveBoat, user } = useAuth();
  const [startDate, setStartDate] = useState(() => new Date());
  const [numWeeks, setNumWeeks] = useState(3);
  const [plans, setPlans] = useState({});
  const [recipes, setRecipes] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [customMeal, setCustomMeal] = useState("");
  const [recipeFilter, setRecipeFilter] = useState("");
  const [recipeFilterMode, setRecipeFilterMode] = useState("same"); // "same" | "all"
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [aiPlanOpen, setAiPlanOpen] = useState(false);
  const [smartSuggestOpen, setSmartSuggestOpen] = useState(false);
  const [dragOver, setDragOver] = useState(null);
  const dragRef = useRef(null);
  // Mobile-friendly "Move meal" picker — { fromDate, fromMeal } | null
  const [movePickerSrc, setMovePickerSrc] = useState(null);
  // Per-shift portion-scaling (boat.shiftN.num_onboard, fallback boat.num_onboard, fallback 6)
  const [shiftServingsLocal, setShiftServingsLocal] = useState(6);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarSearch, setSidebarSearch] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeShift, setActiveShift] = useState("1");
  const [activeTrip, setActiveTrip] = useState(null);
  const [allTrips, setAllTrips] = useState([]);
  // Quick-create trip-record dialog (auto-filled from current shift+trip view)
  const [createTripOpen, setCreateTripOpen] = useState(false);
  const [createTripForm, setCreateTripForm] = useState({ name: "", budget: "", end_date: "" });
  const [creatingTrip, setCreatingTrip] = useState(false);
  const [boatImage, setBoatImage] = useState("");
  const [boatData, setBoatData] = useState(null);
  const [tripNumber, setTripNumber] = useState(1);
  const [boatLoaded, setBoatLoaded] = useState(false);
  const [addExtraOpen, setAddExtraOpen] = useState(false);
  const [extraName, setExtraName] = useState("");
  const [extraMeal, setExtraMeal] = useState("dinner"); // "lunch" | "dinner"
  const [extraTargetDate, setExtraTargetDate] = useState(null); // which day the new tab belongs to
  const [layoutDialogOpen, setLayoutDialogOpen] = useState(false);
  const { layout: boatLayout, update: updateBoatLayout, reset: resetBoatLayout } = useLayout("boat");

  // Normalise extra_slots to [{name, meal}] - accepts legacy strings, maps Norwegian aliases
  const normalizeExtraSlots = (raw) => (raw || []).map((s) => {
    const aliases = { lunsj: "lunch", middag: "dinner" };
    if (typeof s === "string") return { name: s, meal: "dinner" };
    const meal = aliases[s.meal] || s.meal || "dinner";
    return { name: s.name, meal };
  });

  const addExtraSlot = async () => {
    const name = extraName.trim();
    if (!name) return;
    if (!extraTargetDate) { toast.error("Velg en dag først"); return; }
    // Per-day extra: add to DayPlan.extra_labels only, not boat-level
    const id = slotIdOf(name);
    const ws = getWs(extraTargetDate); const dk = getDk(extraTargetDate);
    const plan = plans[ws] || { week_start: ws };
    const day = plan[dk] || {};
    const labels = { ...(day.extra_labels || {}) };
    if (labels[id]) {
      toast.error(`'${name}' finnes allerede for denne dagen`);
      return;
    }
    labels[id] = { name, meal: extraMeal };
    const updatedDay = { ...day, extra_labels: labels };
    const updated = { ...plan, [dk]: updatedDay };
    setPlans((p) => ({ ...p, [ws]: updated }));
    hasChangesRef.current = true;
    setHasChanges(true);
    toast.success(`'${name}' lagt til ${DAY_NAMES_FULL[extraTargetDate.getDay()].toLowerCase()}`);
    setExtraName(""); setAddExtraOpen(false); setExtraTargetDate(null);
  };

  const removeExtraSlot = async (slotName, slotMeal, targetDate, dayScope) => {
    if (!window.confirm(`Fjern fanen "${slotName}" fra ${slotMeal === "lunch" ? "lunsj" : "middag"} ${targetDate ? "på " + DAY_NAMES_FULL[targetDate.getDay()].toLowerCase() : ""}? Innhold som allerede er lagt inn forblir i databasen.`)) return;
    if (dayScope && targetDate) {
      // Per-day: remove from this day's extra_labels
      const ws = getWs(targetDate); const dk = getDk(targetDate);
      const plan = plans[ws] || { week_start: ws };
      const day = plan[dk] || {};
      const labels = { ...(day.extra_labels || {}) };
      const id = slotIdOf(slotName);
      delete labels[id];
      const updatedDay = { ...day, extra_labels: labels };
      const updated = { ...plan, [dk]: updatedDay };
      setPlans((p) => ({ ...p, [ws]: updated }));
      hasChangesRef.current = true;
      setHasChanges(true);
      toast.success(`'${slotName}' fjernet fra denne dagen`);
      return;
    }
    // Legacy boat-wide removal (backward compat)
    const boatId = activeBoat?.id || boatData?.id;
    if (!boatId) return;
    const current = normalizeExtraSlots(activeBoat?.extra_slots || boatData?.extra_slots);
    const next = current.filter((s) => !(s.name === slotName && s.meal === slotMeal));
    try {
      await api.updateExtraSlots(boatId, next);
      const boat = (await api.getActiveBoat()).data;
      setBoatData(boat);
      toast.success(`'${slotName}' fjernet fra alle dager (gammel fane)`);
    } catch (err) { toast.error(err.response?.data?.detail || "Feil"); }
  };

  // Load boat data + user preferred shift once on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [boatRes, meRes] = await Promise.allSettled([api.getActiveBoat(), api.getMe()]);
        if (!cancelled) {
          const boat = boatRes.status === "fulfilled" ? boatRes.value.data : null;
          setBoatData(boat || null);
          setBoatImage(boat?.image_url || "");
          if (meRes.status === "fulfilled") {
            const pref = meRes.value.data?.preferred_shift;
            if (pref === "1" || pref === "2") {
              setActiveShift(pref);
              try { localStorage.setItem("pb-active-shift", pref); } catch { /* ignore */ }
            }
          }
          setBoatLoaded(true);
        }
      } catch {
        if (!cancelled) setBoatLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Sync boat image whenever active boat changes in context (boat switcher, image upload, etc.)
  useEffect(() => {
    if (activeBoat) {
      setBoatData(activeBoat);
      setBoatImage(activeBoat.image_url || "");
    }
  }, [activeBoat]);

  const switchShift = async (newShift) => {
    if (hasChanges && !window.confirm("Du har ulagrede endringer. Bytte skift?")) return;
    setActiveShift(newShift);
    try { localStorage.setItem("pb-active-shift", newShift); } catch { /* ignore */ }
    // Always jump to the computed CURRENT trip for the new shift — same
    // behaviour as cold-load. Stale saved values would otherwise show a
    // future/past trip the user navigated to in a previous session.
    if (boatData) {
      const current = computeCurrentTripNumber(newShift, boatData);
      setTripNumber(current);
    }
    // Only persist preferred_shift for admins (regular crew stays locked).
    if (user?.role === "admin") {
      try { await api.setPreferredShift(newShift); }
      catch (err) { console.warn("Kunne ikke lagre skift-valg:", err?.message); }
    }
  };

  // Create a Trip-record (name + budget) prefilled from current Turnusplan view
  // (shift, trip_number, start_date). Backend's create_trip will mirror these
  // values into boat.shift_config — but they're already aligned by definition.
  const handleCreateTripRecord = async () => {
    if (!createTripForm.name.trim()) { toast.error("Namn er påkrevd"); return; }
    if (!boatData) return;
    setCreatingTrip(true);
    try {
      const startStr = getShiftStartDate(activeShift, tripNumber, boatData).toISOString().slice(0, 10);
      await api.createTrip({
        name: createTripForm.name.trim(),
        start_date: startStr,
        end_date: createTripForm.end_date || "",
        budget: parseFloat(createTripForm.budget) || 0,
        shift: activeShift,
        trip_number: tripNumber,
      });
      toast.success(`Tur ${tripNumber} (Skift ${activeShift}) oppretta`);
      setCreateTripOpen(false);
      setCreateTripForm({ name: "", budget: "", end_date: "" });
      await loadData();
      if (loadActiveBoat) await loadActiveBoat();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Feil ved oppretting");
    } finally {
      setCreatingTrip(false);
    }
  };

  // Save current trip selection back to boat shift_config so it becomes the
  // authoritative start point. Uses today's date as the start of this trip
  // — clicking "Sett som startpunkt" means "this trip starts NOW".
  const saveCurrentTripAsStart = async () => {
    if (!boatData) return;
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    const startStr = `${yyyy}-${mm}-${dd}`;
    const existing = activeShift === "1" ? (boatData.shift1 || {}) : (boatData.shift2 || {});
    const payload = {};
    payload[`shift${activeShift}`] = {
      start_date: startStr,
      cycle_days: existing.cycle_days || 21,
      first_trip_number: tripNumber,
    };
    try {
      await api.updateShiftConfig(boatData.id, payload);
      toast.success(`Skift ${activeShift} · Tur ${tripNumber} starter i dag (${today.toLocaleDateString("nb-NO")})`);
      // Reload boat so shift_info reflects the new config
      if (loadActiveBoat) await loadActiveBoat();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke lagre");
    }
  };

  // Calculate start date from shift config + trip number.
  // If TODAY is inside the requested trip's date-range and we only show a
  // single week (numWeeks < cycle), snap startDate to the MONDAY of today's
  // position within the trip — so a 21-day trip seen on day 8 will show the
  // SECOND week, not the first.
  const getShiftStartDate = useCallback((shift, tripNum, boat) => {
    if (!boat) return new Date();
    const config = shift === "1" ? (boat.shift1 || {}) : (boat.shift2 || {});
    const baseDate = config.start_date ? new Date(config.start_date) : new Date();
    const cycleDays = config.cycle_days || 21;
    const firstTrip = config.first_trip_number || 1;
    // Each shift's OWN trip recurs every (cycle_days × 2) days because the
    // two shifts alternate. Trip-numbers for the same shift therefore step
    // by 2 (e.g. skift 2: 3 → 5 → 7), and the day-offset between same-shift
    // trips is cycle_days × 2.
    const shiftStep = 2;
    const tripOffsetForShift = Math.floor((tripNum - firstTrip) / shiftStep);
    const offset = tripOffsetForShift * cycleDays * 2;
    const tripStart = new Date(baseDate);
    tripStart.setDate(tripStart.getDate() + offset);
    tripStart.setHours(0, 0, 0, 0);
    // The shift's own active window is twice cycle_days long (cycle_days "on"
    // + cycle_days "off"). We keep snapping to the Monday of THIS calendar
    // week as long as today is anywhere inside that span — so when the crew
    // are still onboard past the formal trip end, the calendar keeps showing
    // the current week instead of bouncing back to week 1.
    const tripEnd = new Date(tripStart);
    tripEnd.setDate(tripEnd.getDate() + cycleDays * 2);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (today >= tripStart && today < tripEnd && (numWeeks || 1) * 7 < cycleDays * 2) {
      const dow = today.getDay();
      const mondayOffset = dow === 0 ? -6 : 1 - dow;
      const monday = new Date(today);
      monday.setDate(today.getDate() + mondayOffset);
      return monday < tripStart ? tripStart : monday;
    }
    return tripStart;
  }, [numWeeks]);

  // Compute which trip number currently contains today's date FOR THIS SHIFT.
  // We step by 2 trips every (cycle_days × 2) days so that skift 1 keeps its
  // odd/even parity (and vice versa for skift 2) — never auto-jumping into
  // the other shift's trip.
  const computeCurrentTripNumber = useCallback((shift, boat) => {
    if (!boat) return 1;
    const config = shift === "1" ? (boat.shift1 || {}) : (boat.shift2 || {});
    if (!config.start_date) return 1;
    const baseDate = new Date(config.start_date); baseDate.setHours(0,0,0,0);
    const cycleDays = config.cycle_days || 21;
    const firstTrip = config.first_trip_number || 1;
    const today = new Date(); today.setHours(0,0,0,0);
    const diffDays = Math.floor((today - baseDate) / 86400000);
    if (diffDays < 0) return firstTrip;
    const shiftCycleDays = cycleDays * 2;
    return firstTrip + Math.floor(diffDays / shiftCycleDays) * 2;
  }, []);


  // Auto-jump to the currently active trip on mount + whenever shift changes.
  // The active trip is derived from the boat's shift_config (start_date +
  // cycle_days + first_trip_number) which is per-BOAT — shared across all
  // users, browsers and PCs. As soon as today's date crosses into the next
  // trip's range, the page automatically rolls over.
  //
  // We intentionally do NOT use per-user `preferred_trips` or localStorage
  // here — clicking +/- is a transient preview of past/future trips, and
  // refresh/return must always default back to the live "active" trip.
  //
  // Track which boat we've snapped to so switching boat re-computes for the
  // new boat. Earlier this used a single boolean which got stuck `true` after
  // the first boat loaded — so when the user switched boat the page kept
  // showing the previous boat's tripNumber. Boat-isolation bug.
  const [snappedBoatId, setSnappedBoatId] = useState(null);
  useEffect(() => {
    if (boatData && boatData.id && boatData.id !== snappedBoatId) {
      const computed = computeCurrentTripNumber(activeShift, boatData);
      setTripNumber(computed);
      setSnappedBoatId(boatData.id);
    }
  }, [boatData, activeShift, snappedBoatId, computeCurrentTripNumber]);

  // When user switches shift, re-snap to that shift's active trip.
  useEffect(() => {
    if (boatData) {
      const computed = computeCurrentTripNumber(activeShift, boatData);
      setTripNumber(computed);
    }
    // We intentionally depend only on activeShift here — manual +/-
    // changes to tripNumber must NOT re-trigger this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeShift]);

  // Update startDate when shift or trip number changes
  useEffect(() => {
    if (boatData) {
      const d = getShiftStartDate(activeShift, tripNumber, boatData);
      setStartDate(d);
    }
  }, [activeShift, tripNumber, boatData, getShiftStartDate]);

  // Keep shift-servings input in sync with the active shift on the loaded boat
  useEffect(() => {
    if (!boatData) return;
    const cfg = activeShift === "1" ? (boatData.shift1 || {}) : (boatData.shift2 || {});
    const value = (cfg.num_onboard && cfg.num_onboard > 0) ? cfg.num_onboard : (boatData.num_onboard || 6);
    setShiftServingsLocal(value);
  }, [boatData, activeShift]);

  // Only boat admins / owner may change the per-shift crew count
  const myRole = activeBoat?.my_access?.role || activeBoat?.my_role || "";
  const canEditShiftServings = ["owner", "admin"].includes(myRole) || user?.role === "admin";

  const persistShiftServings = async () => {
    if (!boatData || !canEditShiftServings) return;
    const cfg = activeShift === "1" ? (boatData.shift1 || {}) : (boatData.shift2 || {});
    const current = (cfg.num_onboard && cfg.num_onboard > 0) ? cfg.num_onboard : (boatData.num_onboard || 6);
    if (shiftServingsLocal === current) return;
    try {
      const payload = {};
      payload[`shift${activeShift}`] = { ...cfg, num_onboard: shiftServingsLocal };
      await api.updateShiftConfig(boatData.id, payload);
      toast.success(`Skift ${activeShift}: ${shiftServingsLocal} personer lagret`);
      if (loadActiveBoat) await loadActiveBoat();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke lagre");
    }
  };

  const totalDays = numWeeks * 7;
  const allDays = getDays(startDate, numWeeks);
  const [showPastDays, setShowPastDays] = useState(false);
  const startOfToday = (() => { const t = new Date(); t.setHours(0,0,0,0); return t; })();
  const futureOrTodayDays = allDays.filter((d) => d >= startOfToday);
  // If the entire trip is in the past, fall back to showing all days (otherwise screen would be empty)
  const allInPast = futureOrTodayDays.length === 0;
  const visibleDays = (showPastDays || allInPast) ? allDays : futureOrTodayDays;
  const hiddenPastCount = allInPast ? 0 : allDays.length - visibleDays.length;
  const weeks = groupIntoWeeks(visibleDays);

  const getWs = (d) => { const date = new Date(d); const day = date.getDay(); date.setDate(date.getDate() - day + (day === 0 ? -6 : 1)); return dateToKey(date); };
  const getDk = (d) => ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"][d.getDay()];
  // Derive unique week-starts deterministically from the resolved day list.
  // We depend on the STRING representation of startDate (not the Date object)
  // so identical re-renders don't create a new array reference. This is
  // critical because `loadData` depends on `uniqueWs` — and `loadData`
  // OVERWRITES local plan state, so an accidental re-run wipes the user's
  // unsaved edits while the auto-save debounce is still pending.
  const startDateKey = dateToKey(startDate);
  const uniqueWs = useMemo(() => {
    const s = new Set();
    allDays.forEach((d) => s.add(getWs(d)));
    return [...s];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDateKey, numWeeks]);

  // Track whether we have unsaved edits via a ref. The state version triggers
  // re-renders for the save-button UI, but `loadData` runs as a useEffect
  // dependency, so it always sees the captured (stale) value. We need the
  // ref to make the latest value available inside `loadData`.
  const hasChangesRef = useRef(false);

  const loadData = useCallback(async () => {
    // Don't clobber unsaved edits — wait for the auto-save debounce to flush
    // first. The auto-save effect will trigger another loadData() via
    // hasChanges → false → no-op here on the next tick.
    if (hasChangesRef.current) return;
    try {
      const results = await Promise.allSettled([api.getRecipes(false), api.getActiveTrip(), api.getTrips(), ...uniqueWs.map((ws) => api.getMealPlan(ws, activeShift))]);
      const [rr, tripRes, allTripsRes, ...prs] = results;
      if (rr.status === "fulfilled") setRecipes(rr.value.data);
      if (tripRes.status === "fulfilled") setActiveTrip(tripRes.value.data);
      if (allTripsRes.status === "fulfilled") setAllTrips(allTripsRes.value.data || []);
      const np = {};
      uniqueWs.forEach((ws, i) => {
        if (prs[i]?.status === "fulfilled") np[ws] = prs[i].value.data;
      });
      setPlans(np);
      setHasChanges(false);
      hasChangesRef.current = false;
    } catch { toast.error("Kunne ikke laste turnusplan"); }
  }, [uniqueWs, activeShift]);

  useEffect(() => { loadData(); }, [loadData]);

  const extraSlots = normalizeExtraSlots(activeBoat?.extra_slots || boatData?.extra_slots);
  const extrasForMeal = (mealKey) => extraSlots.filter((s) => s.meal === mealKey);
  const slotIdOf = (name) => `extra:${name.toLowerCase().replace(/[^a-z0-9]/g, "_")}`;

  // Per-day extra labels (new: per-day tabs). Falls back to boat-level extras for legacy data.
  const dayExtraLabels = (date) => {
    const p = plans[getWs(date)];
    const day = p?.[getDk(date)] || {};
    const labels = day.extra_labels || {};
    return Object.entries(labels).map(([id, v]) => ({ id, name: v.name || "", meal: v.meal || "dinner" }));
  };

  // Build ordered meal list per DAY. For each base meal, interleave extras belonging to that day + any legacy boat-level slots.
  const buildMealOrder = (date) => {
    const perDay = dayExtraLabels(date);
    const result = [];
    MEALS.forEach((m) => {
      result.push({ key: m, type: "base" });
      // Day-specific extras first
      perDay.filter((s) => s.meal === m).forEach((s) => {
        result.push({ key: s.id, type: "extra", name: s.name, meal: m, dayScope: true });
      });
      // Legacy boat-level extras (backward compat — only if day has no per-day counterpart)
      extrasForMeal(m).forEach((s) => {
        const id = slotIdOf(s.name);
        if (!perDay.find((p) => p.id === id)) {
          result.push({ key: id, type: "extra", name: s.name, meal: m, dayScope: false });
        }
      });
    });
    return result;
  };

  const getSlot = (date, meal) => {
    const p = plans[getWs(date)];
    const day = p?.[getDk(date)];
    if (!day) return null;
    if (meal.startsWith("extra:")) return day.extras?.[meal] || null;
    return day[meal] || null;
  };
  const getContent = (date, meal) => { const s = getSlot(date, meal); return s?.recipe_title || s?.custom_meal || null; };

  const setSlot = (date, meal, data) => {
    const ws = getWs(date); const dk = getDk(date);
    setPlans((p) => {
      const plan = p[ws] || { week_start: ws };
      const day = plan[dk] || {};
      let updatedDay;
      if (meal.startsWith("extra:")) {
        updatedDay = { ...day, extras: { ...(day.extras || {}), [meal]: data } };
      } else {
        updatedDay = { ...day, [meal]: data };
      }
      return { ...p, [ws]: { ...plan, [dk]: updatedDay } };
    });
    hasChangesRef.current = true;
    setHasChanges(true);
  };

  const saveAll = useCallback(async () => {
    setSaving(true);
    try {
      await Promise.all(uniqueWs.map((ws) => {
        const plan = plans[ws];
        if (plan) return api.updateMealPlan(ws, plan, activeShift);
        return Promise.resolve();
      }));
      hasChangesRef.current = false;
      setHasChanges(false);
    } catch { toast.error("Feil ved auto-lagring"); }
    finally { setSaving(false); }
  }, [uniqueWs, plans, activeShift]);

  // Auto-save: debounce 1.2s after the last edit, then persist silently.
  useEffect(() => {
    if (!hasChanges) return;
    const t = setTimeout(() => { saveAll(); }, 1200);
    return () => clearTimeout(t);
  }, [hasChanges, plans, saveAll]);

  const openSlotDialog = (date, meal) => { setSelectedSlot({ date, meal }); setCustomMeal(""); setRecipeFilter(""); setRecipeFilterMode("same"); setDialogOpen(true); };

  const assignRecipe = (recipe) => {
    if (!selectedSlot) return; setDialogOpen(false);
    setSlot(selectedSlot.date, selectedSlot.meal, { recipe_id: recipe.id, recipe_title: recipe.title, custom_meal: "" });
    toast.success(`${recipe.title} lagt til`);
  };

  const assignCustomMeal = () => {
    if (!selectedSlot || !customMeal.trim()) return; setDialogOpen(false);
    setSlot(selectedSlot.date, selectedSlot.meal, { recipe_id: null, recipe_title: "", custom_meal: customMeal.trim() });
    toast.success(`${customMeal.trim()} lagt til`);
  };

  const clearSlot = (date, meal) => {
    setSlot(date, meal, { recipe_id: null, recipe_title: "", custom_meal: "" });
  };

  const shiftDays = (dir) => { const d = new Date(startDate); d.setDate(d.getDate() + dir * totalDays); setStartDate(d); };
  const isToday = (d) => { const t = new Date(); return d.getDate()===t.getDate()&&d.getMonth()===t.getMonth()&&d.getFullYear()===t.getFullYear(); };

  // Auto-scroll is intentionally disabled — the page already opens at the current trip
  // and past days are hidden, so today's card is naturally at the top.

  // HTML5 Drag & Drop - internal (slot-to-slot) move
  const handleDragStart = (e, date, meal) => {
    const content = getContent(date, meal);
    if (!content) { e.preventDefault(); return; }
    dragRef.current = { date, meal, slot: getSlot(date, meal) };
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", content);
  };

  // Always clear the pending drag-source ref when the user lifts the pointer,
  // even if they drop outside any slot.
  const handleDragEnd = () => {
    dragRef.current = null;
    setDragOver(null);
  };

  const handleDrop = (e, date, meal) => {
    e.preventDefault(); setDragOver(null);

    // Check if this is a recipe from the sidebar/library
    const recipeId = e.dataTransfer.getData("application/recipe-id");
    const recipeTitle = e.dataTransfer.getData("application/recipe-title");
    if (recipeId && recipeTitle) {
      setSlot(date, meal, { recipe_id: recipeId, recipe_title: recipeTitle, custom_meal: "" });
      toast.success(`${recipeTitle} lagt til`);
      dragRef.current = null;
      return;
    }

    // Internal slot-to-slot swap
    if (!dragRef.current) return;
    const src = dragRef.current;
    const dstSlot = getSlot(date, meal);
    setSlot(date, meal, src.slot);
    setSlot(src.date, src.meal, dstSlot || { recipe_id: null, recipe_title: "", custom_meal: "" });
    dragRef.current = null;
    toast.success("Måltid flyttet");
  };

  // AI suggestion
  const applyAISuggestion = (sugg) => {
    if (!sugg || Object.keys(sugg).length === 0) return;
    const target = (visibleDays && visibleDays.length > 0 ? visibleDays : allDays).slice(0, 21);
    const dks = Object.keys(sugg);
    let filled = 0;
    for (let i = 0; i < Math.min(dks.length, target.length); i++) {
      const ds = sugg[dks[i]];
      for (const meal of MEALS) {
        if (!ds || !ds[meal]) continue;
        const rid = ds[meal].recipe_id;
        const title = ds[meal].recipe_title || "";
        const cm = ds[meal].custom_meal || "";
        if (!rid || rid === "custom_meal" || rid === "null" || rid === "") {
          setSlot(target[i], meal, { recipe_id: null, recipe_title: "", custom_meal: cm || title || "" });
        } else {
          setSlot(target[i], meal, { recipe_id: rid, recipe_title: title, custom_meal: "" });
        }
        filled++;
      }
    }
    setHasChanges(true);
    hasChangesRef.current = true;
    toast.success(`${filled} måltider lagt inn`);
  };

  const handleSuggest = async () => {
    setSuggestLoading(true);
    try {
      const res = await api.suggestMenu({ days: 7, servings });
      const sugg = res.data.suggestion;
      if (!sugg || Object.keys(sugg).length === 0) { toast.error("Ingen forslag mottatt"); return; }
      // Use visible (future-or-today) days first, so the suggestion lands where the user is looking
      const target = (visibleDays && visibleDays.length > 0 ? visibleDays : allDays).slice(0, 7);
      const dks = Object.keys(sugg);
      let filled = 0;
      for (let i = 0; i < Math.min(dks.length, target.length); i++) {
        const ds = sugg[dks[i]];
        for (const meal of MEALS) {
          if (!ds || !ds[meal]) continue;
          const rid = ds[meal].recipe_id;
          const title = ds[meal].recipe_title || "";
          // AI sometimes returns "custom_meal" as recipe_id for free-text suggestions
          if (!rid || rid === "custom_meal" || rid === "null" || rid === "") {
            setSlot(target[i], meal, { recipe_id: null, recipe_title: "", custom_meal: title || ds[meal].custom_meal || "" });
          } else {
            setSlot(target[i], meal, { recipe_id: rid, recipe_title: title, custom_meal: "" });
          }
          filled++;
        }
      }
      setHasChanges(true);
      hasChangesRef.current = true;
      toast.success(`AI-menyforslag: ${filled} måltider lagt inn. Husk å lagre.`);
    } catch (err) {
      console.error("AI suggest failed:", err);
      toast.error(err.response?.data?.detail || "Kunne ikke generere menyforslag");
    }
    finally { setSuggestLoading(false); }
  };

  const handleResetMenu = async () => {
    if (!window.confirm("Nullstille hele turens meny? Alle planlagte måltider for Skift " + activeShift + " / Tur " + tripNumber + " blir slettet. Kan ikke angres.")) return;
    try {
      const startKey = dateToKey(allDays[0]);
      const endKey = dateToKey(allDays[allDays.length - 1]);
      const res = await api.resetMealPlans({ shift: activeShift, start_date: startKey, end_date: endKey });
      toast.success(`Menyen er nullstilt (${res.data.deleted} uker fjernet)`);
      // Clear local state and reload from backend
      setPlans({});
      setHasChanges(false);
      hasChangesRef.current = false;
      await loadData();
    } catch {
      toast.error("Kunne ikke nullstille menyen");
    }
  };

  const filteredSidebarRecipes = sidebarSearch
    ? recipes.filter((r) => r.title.toLowerCase().includes(sidebarSearch.toLowerCase()))
    : recipes;

  // Build menu text for print/email
  const buildMenuText = () => {
    const lines = [];
    const shiftLabel = `Skift ${activeShift}`;
    const tripLabel = activeTrip ? activeTrip.name : `Tur ${tripNumber}`;
    lines.push(`UKEMENY — ${shiftLabel} | ${tripLabel}`);
    lines.push(`${formatDate(allDays[0])}.${allDays[0].getFullYear()} – ${formatDate(allDays[allDays.length - 1])}.${allDays[allDays.length - 1].getFullYear()}`);
    lines.push("");
    for (const date of allDays) {
      const dayName = DAY_NAMES_FULL[date.getDay()];
      const dateStr = formatDate(date);
      const meals = MEALS.map((meal) => {
        const c = getContent(date, meal);
        return c ? `  ${MEAL_LABELS[meal]}: ${c}` : null;
      }).filter(Boolean);
      if (meals.length > 0) {
        lines.push(`${dayName} ${dateStr}`);
        meals.forEach((m) => lines.push(m));
        lines.push("");
      }
    }
    return lines.join("\n");
  };

  const handlePrint = () => {
    // Build the print document safely via DOM instead of `document.write` (which can
    // execute arbitrary HTML if any menu line contains angle brackets). We render
    // each piece with `textContent`, so user data can never become markup.
    const text = buildMenuText();
    const lines = text.split("\n");
    const printWin = window.open("", "_blank");
    if (!printWin) {
      // Popup blocked — fall back to an inline print of a temporary element.
      console.warn("Kunne ikke åpne print-vindu (popup-blokker?)");
      return;
    }
    const doc = printWin.document;
    // Minimal HTML shell — the <style> block is static (no user data)
    doc.open();
    doc.write(`<!DOCTYPE html><html lang="nb"><head><meta charset="utf-8"><title>Ukemeny</title>
      <style>
        body { font-family: 'Segoe UI', sans-serif; padding: 40px; max-width: 700px; margin: 0 auto; color: #1c1917; }
        h1 { font-size: 24px; margin-bottom: 4px; }
        .subtitle { color: #78716c; font-size: 14px; margin-bottom: 24px; }
        .day { font-weight: 700; font-size: 16px; margin-top: 16px; margin-bottom: 4px; border-bottom: 1px solid #e7e5e4; padding-bottom: 4px; }
        .meal { font-size: 14px; color: #44403c; padding-left: 12px; line-height: 1.6; }
        .meal-label { font-weight: 600; color: #78716c; }
        @media print { body { padding: 20px; } }
      </style></head><body></body></html>`);
    doc.close();
    const body = doc.body;
    const h1 = doc.createElement("h1");
    h1.textContent = lines[0] || "";
    body.appendChild(h1);
    if (lines[1]) {
      const sub = doc.createElement("div");
      sub.className = "subtitle";
      sub.textContent = lines[1];
      body.appendChild(sub);
    }
    for (let i = 3; i < lines.length; i++) {
      const line = lines[i];
      if (!line.trim()) continue;
      if (line.startsWith("  ")) {
        const [label, ...rest] = line.trim().split(": ");
        const wrap = doc.createElement("div");
        wrap.className = "meal";
        const lab = doc.createElement("span");
        lab.className = "meal-label";
        lab.textContent = `${label}:`;
        wrap.appendChild(lab);
        wrap.appendChild(doc.createTextNode(` ${rest.join(": ")}`));
        body.appendChild(wrap);
      } else {
        const day = doc.createElement("div");
        day.className = "day";
        day.textContent = line;
        body.appendChild(day);
      }
    }
    printWin.print();
  };

  const handleEmail = () => {
    const text = buildMenuText();
    const subject = encodeURIComponent(`Ukemeny — Skift ${activeShift} | Tur ${tripNumber}`);
    const body = encodeURIComponent(text);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  // Today's menu – find plan for today's date across currently loaded plans
  const today = new Date();
  const todayLabel = `${DAY_NAMES_FULL[today.getDay()]} ${today.getDate()}. ${["januar","februar","mars","april","mai","juni","juli","august","september","oktober","november","desember"][today.getMonth()]} ${today.getFullYear()}`;
  const todayMeals = MEALS.reduce((acc, m) => { acc[m] = getContent(today, m); return acc; }, {});
  const hasAnyTodayMeal = MEALS.some((m) => todayMeals[m]);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="weekly-plan-page">
      {/* ─── Configurable intro widgets ─── */}
      {(() => {
        const sizeOf = (id) => {
          const w = boatLayout.widgets.find((x) => x.id === id);
          return resolveSize(w, boatLayout.density);
        };
        const visible = boatLayout.widgets.filter((w) => !w.hidden);
        const RENDERERS = {
          "weather": () => (
            <WeatherWidget place={activeBoat?.address || null} accent="navy" />
          ),
          "expiry-warnings": () => <ExpiryWarningsBanner />,
        };
        const gridCls = boatLayout.columns === 2 ? "grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" : "space-y-4 mb-4";
        return (
          <div className={gridCls} data-testid="boat-widgets">
            {visible.map((w) => {
              const renderer = RENDERERS[w.id];
              if (!renderer) return null;
              const size = sizeOf(w.id);
              const content = renderer(size);
              if (!content) return null;
              return (
                <div key={w.id} data-widget={w.id} data-size={size}>
                  {content}
                </div>
              );
            })}
          </div>
        );
      })()}

      <div className="flex items-center justify-between mb-4 sm:mb-8 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-stone-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Turnusplan</h1>
          <p className="text-xs sm:text-sm text-stone-700 font-medium mt-1 sm:mt-2" data-testid="today-date-label">
            I dag: <span className="text-navy-800">{todayLabel}</span>
          </p>
          <p className="text-xs sm:text-sm text-stone-500 mt-0.5 sm:mt-1 hidden sm:block">
            {totalDays} dager – dra og slipp måltider mellom dager
            {activeTrip && <span className="ml-2 text-navy-800 font-medium">| {activeTrip.name}</span>}
          </p>
          {/* Shift + Trip selector */}
          <div className="flex items-center gap-3 mt-3 flex-wrap" data-testid="shift-selector">
            <div className="flex items-center gap-1">
              {[{ v: "1", l: "Skift 1" }, { v: "2", l: "Skift 2" }].map((s) => {
                const userShift = user?.preferred_shift;
                const lockedToShift = userShift && user?.role !== "admin";
                const locked = lockedToShift && userShift !== s.v;
                return (
                  <button
                    key={s.v}
                    onClick={() => locked ? null : switchShift(s.v)}
                    disabled={locked}
                    className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all ${activeShift === s.v ? "bg-stone-900 text-white shadow-md" : "bg-stone-100 text-stone-500 hover:bg-stone-200"} ${locked ? "opacity-40 cursor-not-allowed" : ""}`}
                    data-testid={`shift-btn-${s.v}`}
                    title={locked ? `Du er logget inn som skift ${userShift}` : ""}
                  >
                    {s.l}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-2 bg-white border border-stone-200 rounded-lg px-3 py-1.5">
              <span className="text-xs font-medium text-stone-500">Tur nr:</span>
              <button onClick={() => setTripNumber(Math.max(1, tripNumber - 2))} className="w-6 h-6 rounded bg-stone-100 flex items-center justify-center text-stone-600 hover:bg-stone-200 text-sm font-bold" title="Forrige tur for dette skiftet (–2)" data-testid="trip-number-minus">-</button>
              <span className="text-sm font-bold text-stone-800 w-6 text-center" data-testid="trip-number-value">{tripNumber}</span>
              <button onClick={() => setTripNumber(tripNumber + 2)} className="w-6 h-6 rounded bg-stone-100 flex items-center justify-center text-stone-600 hover:bg-stone-200 text-sm font-bold" title="Neste tur for dette skiftet (+2)" data-testid="trip-number-plus">+</button>
            </div>
            {boatData && (() => {
              const cfg = activeShift === "1" ? (boatData.shift1 || {}) : (boatData.shift2 || {});
              const cycleDays = cfg.cycle_days || 21;
              const firstTrip = cfg.first_trip_number || 1;
              if (!cfg.start_date) return null;
              // tripStart for this trip = base + ((tripNumber - firstTrip)/2) * cycleDays*2
              const baseDate = new Date(cfg.start_date);
              const tripOffset = Math.floor((tripNumber - firstTrip) / 2);
              const tripStart = new Date(baseDate);
              tripStart.setDate(tripStart.getDate() + tripOffset * cycleDays * 2);
              const tripEnd = new Date(tripStart);
              tripEnd.setDate(tripEnd.getDate() + cycleDays);
              const fmt = (d) => `${d.getDate()}.${d.getMonth() + 1}`;
              return (
                <span
                  className="text-xs px-2 py-1 rounded bg-emerald-50 border border-emerald-200 text-emerald-800 font-medium"
                  data-testid="trip-period-badge"
                  title={`Tur ${tripNumber} sin formelle periode (${cycleDays} dager)`}
                >
                  Tur {tripNumber}: {fmt(tripStart)} – {fmt(tripEnd)}
                </span>
              );
            })()}
            {boatData && (
              <span className="text-xs text-stone-400" data-testid="shift-start-date">
                Start: {getShiftStartDate(activeShift, tripNumber, boatData).toLocaleDateString("nb-NO")}
              </span>
            )}
            {user?.role === "admin" && uniqueWs.length > 0 && (() => {
              const otherShift = activeShift === "1" ? "2" : "1";
              const handleMove = async () => {
                const start = uniqueWs[0];
                const end = uniqueWs[uniqueWs.length - 1];
                if (!window.confirm(`Flytte denne turen (${start} → ${end}) fra Skift ${activeShift} til Skift ${otherShift}? Eksisterende måltider på skiftet vil flettes inn.`)) return;
                try {
                  const res = await api.moveMealPlansShift(activeShift, otherShift, start, end);
                  toast.success(`${res.data?.moved || 0} ukeplaner flyttet til Skift ${otherShift}`);
                  setActiveShift(otherShift);
                  await loadData();
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Feil ved flytting");
                }
              };
              return (
                <button
                  onClick={handleMove}
                  className="inline-flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-100"
                  data-testid="move-trip-shift-btn"
                  title={`Flytt denne turen til skift ${otherShift}`}
                >
                  Flytt til Skift {otherShift}
                </button>
              );
            })()}
            {boatData?.shift_info?.[`shift${activeShift}`]?.start_date && (() => {
              const si = boatData.shift_info[`shift${activeShift}`];
              const isCurrent = tripNumber === si.current_trip_number;
              return (
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${isCurrent ? "bg-emerald-100 text-emerald-800 border border-emerald-200" : "bg-stone-100 text-stone-600 border border-stone-200"}`}
                  data-testid={`shift-info-badge-${activeShift}`}
                  title={`Tur ${si.current_trip_number}: ${si.current_trip_start} → ${si.current_trip_end}`}
                >
                  {isCurrent
                    ? `🟢 Pågår nå · uke ${si.week_in_cycle}/${si.weeks_total}`
                    : `Tur ${si.current_trip_number} pågår (uke ${si.week_in_cycle}/${si.weeks_total})`}
                </span>
              );
            })()}
            {/* Show linked Trip-record (name + budget) if one exists for this
                shift + trip_number. Clicking navigates to /trips for editing.
                If NO record exists, show a small "create record" button that
                auto-fills shift / trip_number / start_date from current view. */}
            {(() => {
              const linked = allTrips.find(
                (t) => String(t.shift) === String(activeShift) && Number(t.trip_number) === Number(tripNumber)
              );
              if (linked) {
                const budgetStr = linked.budget
                  ? `${Math.round(linked.budget).toLocaleString("nb-NO")} kr`
                  : "uten budsjett";
                return (
                  <button
                    type="button"
                    onClick={() => navigate("/trips")}
                    className="inline-flex items-center gap-1 rounded-full bg-navy-50 hover:bg-navy-100 border border-navy-200 px-2.5 py-1 text-[11px] font-semibold text-navy-800 max-w-[240px] truncate"
                    data-testid="linked-trip-badge"
                    title={`Tur-record: ${linked.name} · ${budgetStr}${linked.end_date ? ` · slutt ${linked.end_date}` : ""}`}
                  >
                    ✓ {linked.name} · {budgetStr}
                  </button>
                );
              }
              return (
                <button
                  type="button"
                  onClick={() => setCreateTripOpen(true)}
                  className="inline-flex items-center gap-1 rounded-full bg-amber-50 hover:bg-amber-100 border border-amber-200 px-2.5 py-1 text-[11px] font-semibold text-amber-800"
                  data-testid="create-trip-record-btn"
                  title={`Opprett ein tur-record for Skift ${activeShift} Tur ${tripNumber} (med budsjett, namn, slutt-dato)`}
                >
                  + Opprett tur-record
                </button>
              );
            })()}
            <button
              onClick={saveCurrentTripAsStart}
              className="inline-flex items-center gap-1 rounded-full bg-navy-800 hover:bg-navy-900 px-2.5 py-1 text-[11px] font-semibold text-white"
              data-testid="save-trip-as-start-btn"
              title="Lagre dette skiftet+turen som offisielt startpunkt (oppdaterer turkalenderen)"
            >
              📌 Sett som startpunkt
            </button>
            <FeedbackButton page="/turnusplan" />
          </div>
        </div>
        <div className="flex items-center gap-2 md:gap-3 flex-wrap justify-end">
          <button onClick={saveAll} disabled={saving || !hasChanges} className={`inline-flex items-center justify-center rounded-md px-3 sm:px-5 py-2 text-xs sm:text-sm font-medium transition-all gap-2 ${hasChanges ? "bg-amber-50 text-amber-700 border border-amber-200" : "border border-emerald-200 bg-emerald-50 text-emerald-700"}`} data-testid="save-plan-btn" title="Endringer lagres automatisk">
            <Save className="h-4 w-4" /> {saving ? "Lagrer..." : hasChanges ? "Lagrer…" : "Auto-lagret"}
          </button>
          {/* Secondary actions: synleg på md+, skjult bak ⋯ på mobil */}
          <button onClick={handlePrint} className="hidden md:inline-flex items-center justify-center rounded-md border border-stone-200 bg-white px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors gap-2" data-testid="print-menu-btn">
            <Printer className="h-4 w-4" /> Skriv ut
          </button>
          <button onClick={handleEmail} className="hidden md:inline-flex items-center justify-center rounded-md border border-stone-200 bg-white px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors gap-2" data-testid="email-menu-btn">
            <Mail className="h-4 w-4" /> Send e-post
          </button>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className={`hidden md:inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors gap-2 ${sidebarOpen ? "bg-navy-800 text-white" : "border border-stone-200 bg-white text-stone-700 hover:bg-stone-50"}`} data-testid="toggle-recipe-sidebar-btn">
            <BookOpen className="h-4 w-4" /> Oppskrifter
          </button>
          <button
            onClick={() => setLayoutDialogOpen(true)}
            className="hidden md:inline-flex items-center justify-center rounded-md border border-stone-200 bg-white p-2 text-stone-600 hover:bg-stone-50 transition-colors"
            data-testid="boat-layout-btn"
            title="Tilpass forsiden"
            aria-label="Tilpass forsiden"
          >
            <SlidersHorizontal className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-2 bg-white border border-stone-200 rounded-lg px-2 sm:px-3 py-1.5">
            <span className="text-[10px] sm:text-xs font-medium text-stone-500">Uker:</span>
            <Select value={String(numWeeks)} onValueChange={(v) => setNumWeeks(Number(v))}>
              <SelectTrigger className="w-12 sm:w-14 h-7 sm:h-8 border-0 p-0 text-sm font-bold text-stone-800" data-testid="weeks-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <SelectItem key={n} value={String(n)} data-testid={`weeks-option-${n}`}>{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Per-shift crew count — drives portion scaling for generate-from-plan */}
          <div className="flex items-center gap-2 bg-white border border-stone-200 rounded-lg px-2 sm:px-3 py-1.5" title="Antall personer på dette skiftet — brukes som standard når handlelista genererast frå ukeplanen">
            <span className="text-[10px] sm:text-xs font-medium text-stone-500">Skift {activeShift}:</span>
            <input
              type="number"
              min="1"
              max="50"
              value={shiftServingsLocal}
              onChange={(e) => setShiftServingsLocal(Math.max(1, parseInt(e.target.value) || 1))}
              onBlur={persistShiftServings}
              disabled={!canEditShiftServings}
              className="w-10 sm:w-12 h-7 sm:h-8 bg-transparent text-sm font-bold text-stone-800 text-center border-0 focus:outline-none disabled:cursor-not-allowed"
              data-testid="shift-servings-input"
            />
            <span className="text-[10px] sm:text-xs text-stone-400">pers.</span>
          </div>
          <button onClick={handleSuggest} disabled={suggestLoading} className="inline-flex items-center justify-center rounded-md bg-orange-700 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-white hover:bg-orange-800 transition-colors gap-1.5 sm:gap-2 disabled:opacity-50" data-testid="suggest-menu-btn">
            {suggestLoading ? <><div className="w-3.5 h-3.5 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> <span className="hidden sm:inline">Genererer...</span></> : <><Sparkles className="h-3.5 w-3.5 sm:h-4 sm:w-4" /> <span className="hidden sm:inline">AI Meny</span><span className="sm:hidden">AI</span></>}
          </button>
          <button onClick={() => setAiPlanOpen(true)} className="inline-flex items-center justify-center rounded-md bg-gradient-to-r from-orange-600 to-red-600 px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold text-white hover:from-orange-700 hover:to-red-700 transition-colors gap-1.5 sm:gap-2" data-testid="ai-meal-plan-btn" title="Velg kategori (sunn/hverdag/tradisjon) og dager, basert på ønsker fra mannskapet">
            <Sparkles className="h-3.5 w-3.5 sm:h-4 sm:w-4" /> <span className="hidden sm:inline">AI Kategoriplan</span><span className="sm:hidden">Kat.plan</span>
          </button>
          <button onClick={() => setSmartSuggestOpen(true)} className="inline-flex items-center justify-center rounded-md bg-gradient-to-r from-violet-600 to-fuchsia-600 px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold text-white hover:from-violet-700 hover:to-fuchsia-700 transition-colors gap-1.5 sm:gap-2" data-testid="smart-meal-suggest-btn" title="AI foreslår middagar basert på det du har i beholdninga – prioriterer varer som snart utløper">
            <Sparkles className="h-3.5 w-3.5 sm:h-4 sm:w-4" /> <span className="hidden sm:inline">Frå lager</span><span className="sm:hidden">Lager</span>
          </button>
          <button onClick={handleResetMenu} className="hidden md:inline-flex items-center justify-center rounded-md border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors gap-2" data-testid="reset-menu-btn" title="Tøm hele turens meny">
            <Trash2 className="h-4 w-4" /> Nullstill meny
          </button>
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <button className="hidden sm:inline-flex items-center justify-center gap-2 rounded-md border border-stone-200 bg-white px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors" data-testid="start-date-picker-btn">
                <CalendarIcon className="h-4 w-4 text-stone-400" />Start: {formatDate(startDate)}.{startDate.getFullYear()}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end"><Calendar mode="single" selected={startDate} onSelect={(d) => { if(d){setStartDate(d);setCalendarOpen(false);} }} /></PopoverContent>
          </Popover>
          {/* Mobile-only ⋯ verktøy-meny */}
          <Popover>
            <PopoverTrigger asChild>
              <button className="md:hidden inline-flex items-center justify-center rounded-md border border-stone-200 bg-white p-2 text-stone-600 hover:bg-stone-50" data-testid="wp-mobile-tools-btn" title="Fleire verktøy" aria-label="Fleire verktøy">
                <SlidersHorizontal className="h-4 w-4" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-1.5" align="end">
              <button onClick={handlePrint} className="w-full text-left px-3 py-2 text-sm text-stone-700 hover:bg-stone-50 rounded-md flex items-center gap-2" data-testid="wp-mobile-print">
                <Printer className="h-4 w-4 text-stone-500" /> Skriv ut
              </button>
              <button onClick={handleEmail} className="w-full text-left px-3 py-2 text-sm text-stone-700 hover:bg-stone-50 rounded-md flex items-center gap-2" data-testid="wp-mobile-email">
                <Mail className="h-4 w-4 text-stone-500" /> Send e-post
              </button>
              <button onClick={() => setSidebarOpen(!sidebarOpen)} className="w-full text-left px-3 py-2 text-sm text-stone-700 hover:bg-stone-50 rounded-md flex items-center gap-2" data-testid="wp-mobile-recipes">
                <BookOpen className="h-4 w-4 text-stone-500" /> Oppskrifter
              </button>
              <button onClick={() => setCalendarOpen(true)} className="w-full text-left px-3 py-2 text-sm text-stone-700 hover:bg-stone-50 rounded-md flex items-center gap-2" data-testid="wp-mobile-startdate">
                <CalendarIcon className="h-4 w-4 text-stone-500" /> Start: {formatDate(startDate)}
              </button>
              <button onClick={() => setLayoutDialogOpen(true)} className="w-full text-left px-3 py-2 text-sm text-stone-700 hover:bg-stone-50 rounded-md flex items-center gap-2" data-testid="wp-mobile-layout">
                <SlidersHorizontal className="h-4 w-4 text-stone-500" /> Tilpass forside
              </button>
              <div className="my-1 border-t border-stone-100" />
              <button onClick={handleResetMenu} className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md flex items-center gap-2" data-testid="wp-mobile-reset">
                <Trash2 className="h-4 w-4" /> Nullstill meny
              </button>
            </PopoverContent>
          </Popover>
          <div className="flex items-center gap-1 sm:gap-2 bg-white border border-stone-200 rounded-xl px-2 sm:px-3 py-1.5 sm:py-2">
            <button onClick={() => shiftDays(-1)} className="p-1 hover:bg-stone-100 rounded-md transition-colors" data-testid="prev-turnus-btn"><ChevronLeft className="h-4 w-4 text-stone-500" /></button>
            <span className="text-[11px] sm:text-sm font-medium text-stone-700 min-w-[90px] sm:min-w-[140px] text-center" data-testid="turnus-range">{formatDate(allDays[0])} – {formatDate(allDays[allDays.length - 1])}</span>
            <button onClick={() => shiftDays(1)} className="p-1 hover:bg-stone-100 rounded-md transition-colors" data-testid="next-turnus-btn"><ChevronRight className="h-4 w-4 text-stone-500" /></button>
          </div>
        </div>
      </div>

      <div className="flex gap-6">
        {/* Recipe Sidebar for Drag & Drop */}
        {sidebarOpen && (
          <div className="w-64 flex-shrink-0 animate-fade-in" data-testid="recipe-sidebar">
            <div className="sticky top-4 bg-white border border-stone-200 rounded-xl p-4 max-h-[80vh] overflow-hidden flex flex-col">
              <h3 className="text-sm font-semibold text-stone-700 uppercase tracking-wider mb-3 flex items-center gap-2">
                <GripVertical className="h-3.5 w-3.5 text-stone-400" /> Dra oppskrifter inn
              </h3>
              <div className="relative mb-3">
                <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-stone-400" />
                <input
                  value={sidebarSearch}
                  onChange={(e) => setSidebarSearch(e.target.value)}
                  placeholder="Søk..."
                  className="w-full rounded-lg border border-stone-200 bg-stone-50 pl-8 pr-3 py-1.5 text-xs focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none"
                  data-testid="sidebar-recipe-search"
                />
              </div>
              <div className="overflow-y-auto flex-1 space-y-1.5">
                {filteredSidebarRecipes.length === 0 ? (
                  <p className="text-xs text-stone-400 text-center py-4">Ingen oppskrifter funnet</p>
                ) : filteredSidebarRecipes.map((r) => (
                  <div
                    key={r.id}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData("application/recipe-id", r.id);
                      e.dataTransfer.setData("application/recipe-title", r.title);
                      e.dataTransfer.effectAllowed = "copy";
                    }}
                    className="flex items-center gap-2 p-2 rounded-lg border border-stone-100 bg-stone-50 hover:bg-sailcloth-100 hover:border-navy-100 cursor-grab active:cursor-grabbing transition-all group"
                    data-testid={`sidebar-recipe-${r.id}`}
                  >
                    <GripVertical className="h-3.5 w-3.5 text-stone-300 group-hover:text-seawater-500 flex-shrink-0 transition-colors" />
                    <div className="min-w-0 flex-1">
                      <span className="text-xs font-medium text-stone-700 block truncate">{r.title}</span>
                      <span className="text-[10px] text-stone-400">{r.category || r.meal_type}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Main calendar */}
        <div className="flex-1 space-y-8">
          {allInPast && (
            <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5" data-testid="all-past-banner">
              <span className="text-xs text-amber-800">
                Hele turen er ferdig. Bruk "+" over for å gå til neste tur, eller endre startdato.
              </span>
            </div>
          )}
          {hiddenPastCount > 0 && !allInPast && (
            <div className="flex items-center justify-between bg-sailcloth-100/60 border border-stone-200 rounded-xl px-4 py-2.5" data-testid="hidden-past-banner">
              <span className="text-xs text-stone-600">
                {showPastDays ? `Viser alle ${allDays.length} dager` : `${hiddenPastCount} tidligere ${hiddenPastCount === 1 ? "dag" : "dager"} er skjult`}
              </span>
              <button
                onClick={() => setShowPastDays((v) => !v)}
                className="text-xs font-medium text-navy-800 hover:underline"
                data-testid="toggle-past-days"
              >
                {showPastDays ? "Skjul tidligere" : "Vis tidligere"}
              </button>
            </div>
          )}
          {weeks.map((week, weekIdx) => (
            <section key={week[0] ? week[0].toISOString().slice(0, 10) : `week-${weekIdx}`} className="animate-fade-in" style={{ animationDelay: `${weekIdx * 0.1}s` }} data-testid={`turnus-week-${weekIdx}`}>
              <div className="flex items-center gap-3 mb-3">
                <h2 className="text-xl font-semibold text-stone-800" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Uke {getWeekNumber(week[0])}</h2>
                <span className="text-sm text-stone-400">{formatDate(week[0])} – {formatDate(week[week.length - 1])}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
                {week.map((date) => (
                  <div key={dateToKey(date)} className={`border rounded-xl p-3 flex flex-col transition-all relative overflow-hidden ${isToday(date) ? "border-seawater-500 ring-2 ring-seawater-500 shadow-lg shadow-navy-100/50" : "border-stone-100"}`} data-testid={`day-card-${dateToKey(date)}`}>
                    {/* Boat background */}
                    {boatImage && (
                      <div className="absolute inset-0 pointer-events-none z-0">
                        <img src={boatImage} alt="" className="w-full h-full object-cover opacity-[0.12]" />
                      </div>
                    )}
                    <div className="relative z-10">
                    {isToday(date) && (
                      <span className="absolute -top-1 -right-1 bg-navy-800 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wider shadow-sm" data-testid="today-badge">I dag</span>
                    )}
                    <div className="flex items-center justify-between mb-2 pb-1.5 border-b border-stone-100">
                      <h3 className="text-base font-semibold text-stone-700" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{DAY_NAMES_FULL[date.getDay()]}</h3>
                      <span className={`text-xs font-medium ${isToday(date) ? "text-navy-800 bg-sailcloth-100 px-1.5 py-0.5 rounded-full" : "text-stone-400"}`}>{formatDate(date)}</span>
                    </div>
                    <div className="space-y-1.5 flex-1">
                      {buildMealOrder(date).map((entry) => {
                        const meal = entry.key;
                        const isExtra = entry.type === "extra";
                        const extraLabel = isExtra ? entry.name : null;
                        const extraMealOwner = isExtra ? entry.meal : null;
                        const extraDayScope = isExtra ? entry.dayScope : false;
                        const content = getContent(date, meal);
                        const slotKey = `${dateToKey(date)}|${meal}`;
                        const isDessert = meal === "dessert";
                        return (
                          <div
                            key={meal}
                            className={`meal-slot rounded-lg border p-2 transition-all ${isDessert ? "ml-2 border-l-2 border-l-amber-300" : ""} ${isExtra ? "ml-3 border-l-2 border-l-sky-300 bg-sky-50/20" : ""} ${dragOver === slotKey ? "border-seawater-500 bg-sailcloth-100/50 ring-1 ring-navy-100" : isDessert ? "border-stone-100 bg-amber-50/30" : "border-stone-100"}`}
                            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = dragRef.current ? "move" : "copy"; setDragOver(slotKey); }}
                            onDragLeave={() => setDragOver(null)}
                            onDrop={(e) => handleDrop(e, date, meal)}
                            data-testid={`slot-${dateToKey(date)}-${meal}`}
                          >
                            <div className="flex items-center justify-between mb-0.5">
                              <span className={`text-[10px] uppercase tracking-wider font-semibold ${isDessert ? "text-amber-500" : isExtra ? "text-sky-600" : "text-stone-400"}`}>
                                {isExtra ? extraLabel : MEAL_LABELS[meal]}
                              </span>
                              {isExtra && (
                                <button
                                  onClick={() => removeExtraSlot(extraLabel, extraMealOwner, date, extraDayScope)}
                                  className="p-0.5 rounded text-sky-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                                  title={`Fjern '${extraLabel}'${extraDayScope ? " fra denne dagen" : " (global)"}`}
                                  data-testid={`remove-extra-${slotIdOf(extraLabel)}-${extraMealOwner}`}
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              )}
                            </div>
                            {content ? (
                              <div
                                className="flex items-center justify-between gap-1 group cursor-grab active:cursor-grabbing"
                                draggable
                                onDragStart={(e) => handleDragStart(e, date, meal)}
                                onDragEnd={handleDragEnd}
                              >
                                <div className="flex items-center gap-1 flex-1 min-w-0">
                                  <GripVertical className="h-3 w-3 text-stone-300 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                                  <span className="text-xs font-medium text-stone-700 truncate">{content}</span>
                                </div>
                                <button
                                  onClick={() => setMovePickerSrc({ date, meal })}
                                  className="p-0.5 hover:bg-sailcloth-100 rounded text-stone-400 hover:text-navy-800 transition-colors flex-shrink-0 md:opacity-0 md:group-hover:opacity-100"
                                  title="Flytt til en annen dag/måltid"
                                  data-testid={`move-${dateToKey(date)}-${meal}`}
                                >
                                  <Move className="h-3 w-3" />
                                </button>
                                <button onClick={() => clearSlot(date, meal)} className="p-0.5 hover:bg-stone-100 rounded text-stone-400 hover:text-stone-600 transition-colors flex-shrink-0 opacity-0 group-hover:opacity-100" data-testid={`clear-${dateToKey(date)}-${meal}`}>
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            ) : (
                              <button onClick={() => openSlotDialog(date, meal)} className="w-full flex items-center justify-center gap-1 py-1 text-[10px] text-stone-400 hover:text-navy-800 hover:bg-sailcloth-100 rounded border border-dashed border-stone-200 hover:border-seawater-500 transition-all" data-testid={`add-meal-${dateToKey(date)}-${meal}`}>
                                <Plus className="h-2.5 w-2.5" />Legg til
                              </button>
                            )}
                          </div>
                        );
                      })}
                      {/* Add new extra slot (only show once per card, visible to all – backend enforces admin) */}
                      <button
                        onClick={() => { setExtraMeal("dinner"); setExtraTargetDate(date); setAddExtraOpen(true); }}
                        className="w-full flex items-center justify-center gap-1 py-1 text-[10px] font-medium text-sky-600 hover:text-sky-800 hover:bg-sky-50 rounded border border-dashed border-sky-200 transition-all"
                        data-testid={`add-extra-slot-${dateToKey(date)}`}
                        title="Legg til ny fane kun for denne dagen (f.eks. Tilbehør, Saus, Salat)"
                      >
                        <Plus className="h-2.5 w-2.5" /> Ny fane
                      </button>
                    </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>

      {/* Add extra slot dialog */}
      <AddExtraSlotDialog
        open={addExtraOpen}
        onOpenChange={setAddExtraOpen}
        extraTargetDate={extraTargetDate}
        extraName={extraName}
        setExtraName={setExtraName}
        extraMeal={extraMeal}
        setExtraMeal={setExtraMeal}
        onSubmit={addExtraSlot}
      />

      <AddMealDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        selectedSlot={selectedSlot}
        customMeal={customMeal}
        setCustomMeal={setCustomMeal}
        onAssignCustom={assignCustomMeal}
        recipes={recipes}
        recipeFilter={recipeFilter}
        setRecipeFilter={setRecipeFilter}
        recipeFilterMode={recipeFilterMode}
        setRecipeFilterMode={setRecipeFilterMode}
        onAssignRecipe={assignRecipe}
      />

      <AIMealPlanDialog open={aiPlanOpen} onOpenChange={setAiPlanOpen} weekStart={visibleDays.length > 0 ? getWs(visibleDays[0]) : getWs(new Date())} onApply={applyAISuggestion} />

      {/* Quick-create Trip-record from Turnusplan (skift+tur prefylt) */}
      <CreateTripDialog
        open={createTripOpen}
        onOpenChange={setCreateTripOpen}
        activeShift={activeShift}
        tripNumber={tripNumber}
        startDateText={boatData ? getShiftStartDate(activeShift, tripNumber, boatData).toLocaleDateString("nb-NO") : ""}
        form={createTripForm}
        setForm={setCreateTripForm}
        creating={creatingTrip}
        onSubmit={handleCreateTripRecord}
      />

      {/* Smart meal suggestions based on pantry/expiry */}
      <SmartMealSuggestDialog
        open={smartSuggestOpen}
        onOpenChange={setSmartSuggestOpen}
        onAddToWeek={(suggestion) => {
          // Find the next empty dinner slot in the visible week range and drop the suggestion there.
          const targets = visibleDays.length > 0 ? visibleDays : allDays;
          for (const d of targets) {
            const current = getContent(d, "dinner");
            if (!current) {
              setSlot(d, "dinner", {
                recipe_id: suggestion.recipe_id || null,
                recipe_title: suggestion.title || "",
                custom_meal: suggestion.recipe_id ? "" : (suggestion.title || ""),
              });
              toast.success(`${suggestion.title} lagt til ${DAY_NAMES_FULL[d.getDay()]}`);
              return;
            }
          }
          toast.info("Alle middagar er allereie planlagt — fjern eit forslag først.");
        }}
      />

      {/* Mobile-friendly "Flytt måltid" dialog (alternativ til drag & drop) */}
      <MoveMealDialog
        movePickerSrc={movePickerSrc}
        onClose={() => setMovePickerSrc(null)}
        targets={visibleDays.length > 0 ? visibleDays : allDays}
        getContent={getContent}
        getSlot={getSlot}
        setSlot={setSlot}
        buildMealOrder={buildMealOrder}
      />

      <HomeLayoutDialog
        open={layoutDialogOpen}
        onOpenChange={setLayoutDialogOpen}
        layout={boatLayout}
        onUpdate={updateBoatLayout}
        onReset={resetBoatLayout}
        edition="boat"
      />
    </div>
  );
}
